import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userRelationshipAPI } from '../../services/api';
import { UserRelationship } from '../../types';
import toast from 'react-hot-toast';

interface UserRelationshipState {
  followers: UserRelationship[];
  following: UserRelationship[];
  collaborators: UserRelationship[];
  loading: boolean;
  error: string | null;
  pagination: {
    followers: { page: number; limit: number; total: number; pages: number };
    following: { page: number; limit: number; total: number; pages: number };
    collaborators: { page: number; limit: number; total: number; pages: number };
  };
}

const initialState: UserRelationshipState = {
  followers: [],
  following: [],
  collaborators: [],
  loading: false,
  error: null,
  pagination: {
    followers: { page: 1, limit: 20, total: 0, pages: 0 },
    following: { page: 1, limit: 20, total: 0, pages: 0 },
    collaborators: { page: 1, limit: 20, total: 0, pages: 0 },
  },
};

export const fetchUserFollowers = createAsyncThunk(
  'userRelationship/fetchUserFollowers',
  async (params: { userId: string; page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await userRelationshipAPI.getUserFollowers(params.userId, params);
      // console.log("response.data", response.data.data)
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch followers');
    }
  }
);

export const fetchUserFollowing = createAsyncThunk(
  'userRelationship/fetchUserFollowing',
  async (params: { userId: string; page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await userRelationshipAPI.getUserFollowing(params.userId, params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch following');
    }
  }
);

export const fetchUserCollaborators = createAsyncThunk(
  'userRelationship/fetchUserCollaborators',
  async (params: { userId: string; page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await userRelationshipAPI.getUserCollaborators(params.userId, params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch collaborators');
    }
  }
);

export const toggleFollow = createAsyncThunk(
  'userRelationship/toggleFollow',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userRelationshipAPI.toggleFollow(userId);
      // console.log(response.data)
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle follow');
    }
  }
);

const userRelationshipSlice = createSlice({
  name: 'userRelationship',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAll: (state) => {
      state.followers = [];
      state.following = [];
      state.collaborators = [];
    },
    updateFollowStatusOptimistic: (state, action: PayloadAction<{
        userId: string;
        isFollowing: boolean;
        listType: 'followers' | 'following' | 'collaborators';
      }>) => {
        const { userId, isFollowing, listType } = action.payload;
        
        // Update the appropriate list
        const updateList = (list: UserRelationship[]) => {
          return list.map(user => 
            user.id === userId 
              ? { ...user, is_following: isFollowing }
              : user
          );
        };
        
        switch (listType) {
          case 'followers':
            state.followers = updateList(state.followers);
            break;
          case 'following':
            if (!isFollowing) {
              // Remove from following list if unfollowed
              state.following = state.following.filter(user => user.id !== userId);
              // Update pagination count
              state.pagination.following.total = Math.max(state.pagination.following.total - 1, 0);
            } else {
              state.following = updateList(state.following);
            }
            break;
          case 'collaborators':
            state.collaborators = updateList(state.collaborators);
            break;
        }
      },
      
      // Add reducer to handle when current user gains a new follower
      addNewFollower: (state, action: PayloadAction<UserRelationship>) => {
        // Add to followers list and update count
        state.followers.unshift(action.payload);
        state.pagination.followers.total += 1;
      },
      
      // Add reducer to remove follower when they unfollow
      removeFollower: (state, action: PayloadAction<string>) => {
        const userId = action.payload;
        state.followers = state.followers.filter(user => user.id !== userId);
        state.pagination.followers.total = Math.max(state.pagination.followers.total - 1, 0);
      },
      
      setInitialCounts: (state, action: PayloadAction<{ followers: number; following: number; collaborators: number }>) => {
        state.pagination.followers.total = action.payload.followers;
        state.pagination.following.total = action.payload.following;
        state.pagination.collaborators.total = action.payload.collaborators;
      },
  },
  extraReducers: (builder) => {
    builder
      // Fetch followers
      .addCase(fetchUserFollowers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserFollowers.fulfilled, (state, action) => {
        state.loading = false;
        // // console.log("action.payload.data", action.payload.data)
        state.followers = action.payload.data == null ? [] : action.payload.data;
        state.pagination.followers = action.payload.pagination;
      })
      .addCase(fetchUserFollowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load followers');
      })
      
      // Fetch following
      .addCase(fetchUserFollowing.fulfilled, (state, action) => {
        state.loading = false;
        state.following = action.payload.data == null ? [] : action.payload.data;
        state.pagination.following = action.payload.pagination;
      })
      
      // Fetch collaborators
      .addCase(fetchUserCollaborators.fulfilled, (state, action) => {
        state.loading = false;
        state.collaborators = action.payload.data == null ? [] : action.payload.data;
        state.pagination.collaborators = action.payload.pagination;
      })
      
      // Toggle follow
      .addCase(toggleFollow.fulfilled, (state, action) => {
        // Fix: Access nested data property
        const isFollowing = action.payload.data ? action.payload.data.following : action.payload.following;
        const targetUserId = action.meta.arg;

        // Helper to update a list
        const updateList = (list: UserRelationship[]) => {
          return list.map(user => 
            user.id === targetUserId 
              ? { 
                  ...user, 
                  is_following: isFollowing,
                  is_following_back: isFollowing
                }
              : user
          );
        };

        // Update all lists
        state.followers = updateList(state.followers);
        state.collaborators = updateList(state.collaborators);
        
        // Handle 'following' list specifically
        if (isFollowing) {
             // We followed someone. 
             state.pagination.following.total += 1;
             state.following = updateList(state.following);
        } else {
            // We unfollowed.
            // Remove from following list
            state.following = state.following.filter(u => u.id !== targetUserId);
            state.pagination.following.total = Math.max(state.pagination.following.total - 1, 0);
        }

        toast.success(isFollowing ? 'User followed!' : 'User unfollowed!');
      })
      .addCase(toggleFollow.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to update follow status');
      })
      
      // Fetch following handlers
      .addCase(fetchUserFollowing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserFollowing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load following');
      })

      // Fetch collaborators handlers
      .addCase(fetchUserCollaborators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCollaborators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load collaborators');
      });
  },
});

export const { 
    clearError, 
    clearAll, 
    updateFollowStatusOptimistic,
    addNewFollower,
    removeFollower,
    setInitialCounts
} = userRelationshipSlice.actions;
export default userRelationshipSlice.reducer;
